import { useMemo } from 'react';
import type { DateRange } from '../../store';
import { datePresets, ALL_TIME_LABEL } from './dateRangeUtils';

interface DateRangePickerProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  minDate?: string;
  maxDate?: string;
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  minDate,
  maxDate,
}: DateRangePickerProps) {
  const isAllTime =
    minDate &&
    maxDate &&
    dateRange.start === minDate &&
    dateRange.end === maxDate;

  const activePreset = useMemo(() => {
    if (isAllTime) {
      return ALL_TIME_LABEL;
    }
    for (const preset of datePresets) {
      const range = preset.getRange();
      if (range.start === dateRange.start && range.end === dateRange.end) {
        return preset.label;
      }
    }
    return null;
  }, [dateRange, isAllTime]);

  const handleAllTimeClick = () => {
    if (minDate && maxDate) {
      onDateRangeChange({ start: minDate, end: maxDate });
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full sm:w-auto">
      <label className="text-sm font-medium text-mid-gray">Período:</label>
      <div className="flex flex-wrap gap-1 mb-1">
        <button
          type="button"
          onClick={handleAllTimeClick}
          disabled={!minDate || !maxDate}
          className={`px-2 py-1 text-xs rounded border transition-colors ${
            activePreset === ALL_TIME_LABEL
              ? 'bg-brand-primary text-white border-brand-primary'
              : 'bg-white text-near-black border-mid-gray hover:border-brand-primary'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {ALL_TIME_LABEL}
        </button>
        {datePresets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => onDateRangeChange(preset.getRange())}
            className={`px-2 py-1 text-xs rounded border transition-colors ${
              activePreset === preset.label
                ? 'bg-brand-primary text-white border-brand-primary'
                : 'bg-white text-near-black border-mid-gray hover:border-brand-primary'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <input
          type="date"
          value={dateRange.start}
          min={minDate}
          max={dateRange.end}
          onChange={(e) =>
            onDateRangeChange({ ...dateRange, start: e.target.value })
          }
          className="w-full sm:w-auto px-3 py-2 border border-mid-gray rounded bg-white text-near-black min-h-[44px]"
          aria-label="Fecha de inicio"
        />
        <span className="text-mid-gray text-center hidden sm:block">a</span>
        <input
          type="date"
          value={dateRange.end}
          min={dateRange.start}
          max={maxDate}
          onChange={(e) =>
            onDateRangeChange({ ...dateRange, end: e.target.value })
          }
          className="w-full sm:w-auto px-3 py-2 border border-mid-gray rounded bg-white text-near-black min-h-[44px]"
          aria-label="Fecha de fin"
        />
      </div>
    </div>
  );
}
